/**
 * AllSourcesTable Component
 *
 * Table view of biblio sources of every type. Mirrors JournalArticlesTable but
 * renders the source-type-agnostic column set defined in ./columns and always
 * offers a "Source Type" column. Article-specific columns (topics, collections,
 * extraction status) are intentionally omitted.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { JournalArticle, formatInstitutions, sourceTypeLabelMap } from "@/services/journalArticlesService"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { formatDate } from "@/utils/dateUtils"
import { TruncatedCell } from "@/components/shared/TruncatedCell"

/** Props for the AllSourcesTable component. */
interface AllSourcesTableProps {
  /** The list of sources to display. */
  sources: JournalArticle[]
  /** Whether the data is currently loading. */
  loading: boolean
  /** Callback when a row is clicked to select a source. */
  onSourceSelect: (source: JournalArticle) => void
  /** The sourceId of the currently selected source, if any. */
  selectedSourceId?: number
  /** Set of column keys that should be visible. When provided, only matching columns render. */
  visibleColumns?: Set<string>
}

/**
 * Column definition for the all-sources table view.
 * Each entry maps a JournalArticle key to its header label and min-width.
 */
interface AllSourcesTableColumn {
  /** The key on the source record this column displays. */
  key: string
  /** Header label text. */
  label: string
  /** Minimum width CSS class. */
  minWidth: string
}

/** All columns that can be rendered in the all-sources table view. */
const ALL_SOURCES_TABLE_COLUMNS: AllSourcesTableColumn[] = [
  { key: "title", label: "Title", minWidth: "min-w-[450px]" },
  { key: "sourceType", label: "Source Type", minWidth: "min-w-[120px]" },
  { key: "authors", label: "Authors", minWidth: "min-w-[200px]" },
  { key: "journaltitle", label: "Journal", minWidth: "min-w-[150px]" },
  { key: "publicationYear", label: "Year", minWidth: "min-w-[60px]" },
  { key: "volume", label: "Volume", minWidth: "min-w-[80px]" },
  { key: "issue", label: "Issue", minWidth: "min-w-[80px]" },
  { key: "pages", label: "Pages", minWidth: "min-w-[80px]" },
  { key: "doi", label: "DOI", minWidth: "min-w-[120px]" },
  { key: "doiUrl", label: "DOI URL", minWidth: "min-w-[100px]" },
  { key: "pubmedId", label: "PubMed ID", minWidth: "min-w-[100px]" },
  { key: "pubmedUrl", label: "PubMed URL", minWidth: "min-w-[100px]" },
  { key: "ihmeNid", label: "IHME NID", minWidth: "min-w-[100px]" },
  { key: "mapSurveyId", label: "MAP Survey ID", minWidth: "min-w-[100px]" },
  { key: "surveyType", label: "Survey Type", minWidth: "min-w-[100px]" },
  { key: "abstract", label: "Abstract", minWidth: "min-w-[200px]" },
  { key: "citation", label: "Citation", minWidth: "min-w-[200px]" },
  { key: "suggestedCitation", label: "Suggested Citation", minWidth: "min-w-[200px]" },
  { key: "url", label: "URL", minWidth: "min-w-[120px]" },
  { key: "city", label: "City", minWidth: "min-w-[100px]" },
  { key: "institution", label: "Institution", minWidth: "min-w-[120px]" },
  { key: "institutions", label: "Institutions", minWidth: "min-w-[120px]" },
  { key: "contacts", label: "Contacts", minWidth: "min-w-[120px]" },
  { key: "reportNumber", label: "Report Number", minWidth: "min-w-[100px]" },
  { key: "researchNotes", label: "Research Notes", minWidth: "min-w-[150px]" },
  { key: "usagePermission", label: "Usage Permission", minWidth: "min-w-[100px]" },
  { key: "communicationDate", label: "Communication Date", minWidth: "min-w-[100px]" },
  { key: "dateUrlVisited", label: "Date URL Visited", minWidth: "min-w-[100px]" },
  { key: "createdBy", label: "Created By", minWidth: "min-w-[100px]" },
  { key: "createdDate", label: "Created Date", minWidth: "min-w-[100px]" },
  { key: "lastModifiedBy", label: "Last Modified By", minWidth: "min-w-[100px]" },
  { key: "lastModifiedDate", label: "Last Modified Date", minWidth: "min-w-[100px]" },
]

/** Keys whose values are plain URLs that should render as clickable links. */
const URL_KEYS = new Set<string>(["doiUrl", "pubmedUrl", "url"])

/** Keys that should render with line-clamp styling in cells. */
const CLAMP_KEYS = new Set<string>(["authors", "abstract", "citation", "suggestedCitation", "researchNotes"])

/**
 * Table view for all biblio sources.
 *
 * Displays the source-type-agnostic columns of each source. Columns can be
 * filtered via the visibleColumns prop.
 *
 * @param props - Component props.
 * @param props.sources - Array of sources to render.
 * @param props.loading - Whether data is being fetched.
 * @param props.onSourceSelect - Callback fired when a row is clicked.
 * @param props.selectedSourceId - The sourceId of the currently selected source.
 * @param props.visibleColumns - Optional set of visible column keys.
 * @returns The rendered all-sources table.
 */
export default function AllSourcesTable({
  sources,
  loading,
  onSourceSelect,
  selectedSourceId,
  visibleColumns,
}: AllSourcesTableProps): React.JSX.Element {
  /**
   * Helper to check if a column should be shown.
   *
   * @param key - The column key to check.
   * @returns True if the column should be rendered.
   */
  const isVisible = (key: string): boolean => {
    if (!visibleColumns) return true
    return visibleColumns.has(key)
  }

  const activeColumns = ALL_SOURCES_TABLE_COLUMNS.filter((col) => isVisible(col.key))

  /**
   * Render a collapsed cell value with a tooltip when the text is clipped.
   *
   * @param value - The string value to render.
   * @returns The cell content, or an em-dash placeholder when empty.
   */
  const renderCollapsedCellWithTooltip = (value: string): React.ReactNode => {
    return value != null && value !== "" ? <TruncatedCell value={value} /> : "—"
  }

  /**
   * Render the value for a given source and column key.
   *
   * @param source - The source record.
   * @param key - The column key to render.
   * @returns The cell content node.
   */
  const renderCellValue = (source: JournalArticle, key: string): React.ReactNode => {
    if (key === "title") {
      return <span className="whitespace-normal cursor-default">{source.title}</span>
    }
    if (key === "sourceType") {
      const value = source.sourceType
      if (!value) return "—"
      return sourceTypeLabelMap[value] ?? value
    }
    if (key === "journaltitle" || key === "citation") {
      const value = source[key]
      if (!value) return "—"
      return <span className="line-clamp-2">{value}</span>
    }
    if (key === "authors") {
      return renderCollapsedCellWithTooltip(source.authors?.map((a) => a.name).join("; ") ?? "")
    }
    if (key === "abstract" || key === "suggestedCitation" || key === "researchNotes") {
      const value = (source[key as keyof JournalArticle] as string) ?? ""
      if (!value) return "—"
      if (key === "abstract") {
        return <span className="line-clamp-2">{value}</span>
      }
      return renderCollapsedCellWithTooltip(value)
    }
    if (URL_KEYS.has(key)) {
      const url = source[key as "doiUrl" | "pubmedUrl" | "url"]
      if (!url) return "—"
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 break-all"
        >
          {url}
        </a>
      )
    }
    if (key === "createdDate") {
      return formatDate(source.createdDate)
    }
    if (key === "lastModifiedDate") {
      return formatDate(source.lastModifiedDate)
    }
    // Reports store institutions as a list; join it rather than String()-ing the array.
    if (key === "institutions") {
      return formatInstitutions(source.institutions) || "—"
    }
    // Fallback: render the raw value, or an em-dash placeholder when empty.
    const value = source[key as keyof JournalArticle]
    return value != null && value !== "" ? String(value) : "—"
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto border border-border/50">
      <Table className={loading ? "h-full" : ""}>
        <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
          <TableRow>
            {activeColumns.map((col) => (
              <TableHead key={col.key} className={col.minWidth}>
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={activeColumns.length || 1}>
                <LoadingSpinner
                  loadingContent="Loading sources..."
                  width="w-[calc(100vw-var(--sidebar-width))]"
                />
              </TableCell>
            </TableRow>
          ) : sources.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={activeColumns.length || 1}
                className="text-center text-muted-foreground py-8"
              >
                No sources found.
              </TableCell>
            </TableRow>
          ) : (
            sources.map((source) => (
              <TableRow
                key={source.sourceId}
                className={`hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer ${
                  source.sourceId === selectedSourceId ? "bg-blue-50 dark:bg-blue-950/30" : ""
                }`}
                onClick={() => onSourceSelect(source)}
              >
                {activeColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={CLAMP_KEYS.has(col.key) ? "max-w-[250px]" : "max-w-[300px]"}
                  >
                    {renderCellValue(source, col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
